import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import config from '@/config'
import { useAuthStore } from '@/store/auth'
import qs from 'qs'

interface RetryConfig extends AxiosRequestConfig {
  _retry?: boolean
}

export const api = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

let refreshPromise: Promise<boolean> | null = null

/**
 * REFRESH TOKEN
 */
const requestRefreshToken = async (): Promise<boolean> => {
  try {
    const { data } = await axios.post(
      `${config.apiUrl}/auth/refresh`,
      {},
      { withCredentials: true }
    )

    if (!data?.access_token) return false

    const auth = useAuthStore()
    auth.accessToken = data.access_token
    localStorage.setItem('access_token', data.access_token)

    return true
  } catch {
    return false
  }
}

export const refreshToken = async (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = requestRefreshToken().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

/**
 * REQUEST INTERCEPTOR
 */
api.interceptors.request.use(config => {
  const auth = useAuthStore()

  if (auth.accessToken) {
    config.headers.Authorization = `Bearer ${auth.accessToken}`
  }

  return config
})

/**
 * RESPONSE INTERCEPTOR
 */
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const auth = useAuthStore()
    const originalRequest = error.config as RetryConfig

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      originalRequest._retry = true

      const refreshed = await refreshToken()

      if (!refreshed) {
        auth.logout()
        return Promise.reject(new Error('Session expired'))
      }

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${auth.accessToken}`
      }

      return api(originalRequest)
    }

    return Promise.reject(error)
  }
)

export interface ModuleAttribute {
  id: string
  slot: 'prefix' | 'suffix' | 'affix'
  value: number
}

export interface ModuleItem {
  id: string
  rarity: number
  price: string
  createdAt: string
  attributes: ModuleAttribute[]
}

export interface ModulesQuery {
  prefixDefinitionIds?: string[]
  suffixDefinitionIds?: string[]
  affixDefinitionIds?: string[]
  prefixQlts?: number[]
  suffixQlts?: number[]
  affixQlts?: number[]
  lotId?: string
  qlt?: number
  minPrice?: number
  maxPrice?: number
}

export const fetchModules = async (query: ModulesQuery) => {
  const { data } = await api.get<ModuleItem[]>('/history-modules', {
    params: query,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
  })
  return data
}
